import { GepEventQueue, QUEUE_STORAGE_KEY } from "./queue";
import {
  getConsent,
  getConsentRevokedAt,
  getMerchantChangedAt,
  getSessionId,
  getStoredMerchant,
  getVisitorId,
  isFirstVisit,
  randomId,
  resetConsentOverride,
  setStoredConsent,
  setStoredMerchant,
  setStoredMerchantChangedAt,
} from "./identity";
import { bootstrapTrackingSession, postEvents } from "./transport";
import { installDataLayerBridge, type MappedDataLayerEvent } from "./datalayer";
import type {
  GepEventsPayload,
  GepQueuedEvent,
  GepTrackerConfig,
  GepTrackerStatus,
} from "./types";

export type {
  GepAuthContext,
  GepQueuedEvent,
  GepTrackerConfig,
  GepTrackerStatus,
} from "./types";
export { QUEUE_STORAGE_KEY };

const DEFAULT_FLUSH_INTERVAL_MS = 5000;
const DEFAULT_BATCH_SIZE = 10;
const DEFAULT_MAX_QUEUE_SIZE = 200;
const HEARTBEAT_INTERVAL_MS = 30 * 1000;
// A single engagement delta can never exceed the heartbeat cadence by much;
// anything larger means the tab was frozen/suspended (bfcache, mobile
// background, screen lock) and resuming must not count the whole gap.
const MAX_ENGAGEMENT_DELTA_MS = HEARTBEAT_INTERVAL_MS * 2;
const AUTH_RETRY_MS = 60 * 1000;
const MAX_RETRY_DELAY_MS = 30 * 1000;
const APP_VERSION = (import.meta.env?.VITE_VERSION as string | undefined) || "";

interface TrackerState {
  config?: GepTrackerConfig;
  status: GepTrackerStatus;
  merchantId?: string;
  signedToken?: string;
  widgetToken?: string;
  queue: GepEventQueue;
  flushTimer?: ReturnType<typeof setInterval>;
  heartbeatTimer?: ReturnType<typeof setInterval>;
  authRetryTimer?: ReturnType<typeof setTimeout>;
  retryTimer?: ReturnType<typeof setTimeout>;
  retryAttempt: number;
  flushing: boolean;
  lastVisibleAt: number;
  engagementAccumulatedMs: number;
  visibilityHandler?: () => void;
  pageHideHandler?: () => void;
  pageShowHandler?: () => void;
}

const state: TrackerState = {
  status: "idle",
  queue: new GepEventQueue(DEFAULT_MAX_QUEUE_SIZE),
  retryAttempt: 0,
  flushing: false,
  lastVisibleAt: Date.now(),
  engagementAccumulatedMs: 0,
};

export function resolveGepEndpoint(): string {
  // Reuse the shared GEP API base (VITE_APP_HOME_URL) instead of a dedicated
  // tracking endpoint env var. Accept either the host root or the `/api/v1/gep`
  // base, matching `api/GepService.ts`.
  const homeUrl = `${import.meta.env?.VITE_APP_HOME_URL || ""}`
    .trim()
    .replace(/\/+$/, "");
  if (homeUrl) {
    const gepBase = homeUrl.endsWith("/api/v1/gep")
      ? homeUrl
      : `${homeUrl}/api/v1/gep`;
    return `${gepBase}/tracking`;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/api/v1/gep/tracking`;
  }
  return "/api/v1/gep/tracking";
}

export function isGepTrackingEnabled(): boolean {
  return import.meta.env?.VITE_GEP_TRACKING_ENABLED !== "false";
}

function batchSize(): number {
  return state.config?.batchSize || DEFAULT_BATCH_SIZE;
}

let lastPageLocation = "";

/**
 * Drops transient Vouch query parameters (`vc*`, e.g. `vcuser_room_number`,
 * `vcenc`) from a tracked URL. These are consumed by app boot and must never be
 * persisted or sent to the tracking endpoint, even before the router
 * canonicalizes the address bar.
 */
function stripTransientQueryParams(url: string): string {
  if (!url) return url;
  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
  let parsed: URL;
  try {
    parsed = new URL(url, isAbsolute ? undefined : "https://gep.local");
  } catch {
    return url;
  }
  let changed = false;
  for (const key of Array.from(parsed.searchParams.keys())) {
    if (key.toLowerCase().startsWith("vc")) {
      parsed.searchParams.delete(key);
      changed = true;
    }
  }
  if (!changed) return url;
  return isAbsolute
    ? parsed.toString()
    : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

function sanitizeEventParams(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = { ...params };
  if (typeof sanitized.page_location === "string") {
    sanitized.page_location = stripTransientQueryParams(
      sanitized.page_location,
    );
  }
  if (typeof sanitized.page_referrer === "string") {
    sanitized.page_referrer = stripTransientQueryParams(
      sanitized.page_referrer,
    );
  }
  return sanitized;
}

function pageContext(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  return {
    page_location: stripTransientQueryParams(window.location.href),
    page_referrer: stripTransientQueryParams(
      lastPageLocation || document?.referrer || "",
    ),
  };
}

export function initGepTracker(config: GepTrackerConfig): void {
  resetGepTrackerForTests();
  // GTM used to create window.dataLayer synchronously in index.html. With GTM
  // removed, create it here so direct dataLayer.push sites never throw, even
  // when first-party tracking is disabled.
  if (typeof window !== "undefined") {
    const target = window as unknown as { dataLayer?: unknown[] };
    target.dataLayer = target.dataLayer || [];
  }
  state.config = config;
  const enabled = config.enabled !== false && isGepTrackingEnabled();
  if (!enabled) {
    state.status = "disabled";
    return;
  }
  state.status = "pending-auth";
  attachListeners();
  installDataLayerBridge((event: MappedDataLayerEvent) =>
    track(event.name, event.params),
  );
  state.flushTimer = setInterval(
    () => void flush(),
    config.flushIntervalMs || DEFAULT_FLUSH_INTERVAL_MS,
  );
  state.heartbeatTimer = setInterval(() => heartbeat(), HEARTBEAT_INTERVAL_MS);
  void resolveAuth();
}

export function setConsent(granted: boolean): void {
  setStoredConsent(granted);
  if (!granted) {
    // Revocation must not leak already-queued analytics: drop them now.
    state.queue.clear();
    return;
  }
  if (state.status === "idle") {
    state.status = "pending-auth";
    void resolveAuth();
  }
}

export function track(
  eventName: string,
  params: Record<string, unknown> = {},
): void {
  if (state.status === "disabled" || !state.config) return;
  if (!getConsent()) return;

  const sanitized = sanitizeEventParams(params);
  if (isFirstVisit()) {
    enqueue("first_visit", { ...pageContext() });
  }
  enqueue(eventName, { ...pageContext(), ...sanitized });
  // Resolve (and rotate after inactivity) the session before touching its
  // last-active time. Calling touchSession() here would refresh the timestamp
  // first and make flush()'s getSessionId() reuse an expired session.
  getSessionId();
  if (state.status === "ready" && state.queue.size() >= batchSize()) {
    void flush();
  }
}

export function trackPageView(): void {
  if (typeof window !== "undefined") {
    const current = stripTransientQueryParams(window.location.href);
    // Dedupe consecutive emissions for the same URL (init + initial afterEach).
    if (current === lastPageLocation) return;
    track("page_view", {
      page_location: current,
      page_referrer: stripTransientQueryParams(
        lastPageLocation || document?.referrer || "",
      ),
    });
    lastPageLocation = current;
  } else {
    track("page_view", {});
  }
}

export function trackEngagement(): void {
  const now = Date.now();
  if (
    typeof document === "undefined" ||
    document.visibilityState === "visible"
  ) {
    const delta = Math.min(
      Math.max(0, now - state.lastVisibleAt),
      MAX_ENGAGEMENT_DELTA_MS,
    );
    state.engagementAccumulatedMs += delta;
  }
  state.lastVisibleAt = now;
  const msec = state.engagementAccumulatedMs;
  if (msec <= 0) return;
  state.engagementAccumulatedMs = 0;
  track("user_engagement", {
    ...pageContext(),
    engagement_time_msec: msec,
  });
}

function enqueue(name: string, params: Record<string, unknown>): void {
  const event: GepQueuedEvent = {
    id: randomId(),
    name,
    timestamp: new Date().toISOString(),
    owner: state.merchantId,
    params: APP_VERSION
      ? { app_version: APP_VERSION, ...params }
      : { ...params },
  };
  state.queue.push(event);
}

function toWireEvent(event: GepQueuedEvent): GepQueuedEvent {
  const wire: GepQueuedEvent = { ...event };
  delete wire.owner;
  return wire;
}

export function refreshGepAuth(): Promise<void> {
  return resolveAuth();
}

async function resolveAuth(): Promise<void> {
  if (!state.config || state.status === "disabled") return;
  try {
    const context = await state.config.getAuthContext();
    state.widgetToken = context.widgetToken;
    if (context.merchantId) {
      const previousMerchant = getStoredMerchant();
      if (previousMerchant && previousMerchant !== context.merchantId) {
        // The persisted queue is origin-wide but credentials are per merchant:
        // never send a previous merchant's guest events under a new merchant.
        state.queue.clear();
        // Durable boundary: even if the purge above could not be persisted,
        // events queued before this instant are discarded on the next load.
        setStoredMerchantChangedAt(new Date().toISOString());
      }
      setStoredMerchant(context.merchantId);
      state.merchantId = context.merchantId;
    }
    if (context.merchantId && context.apiKey) {
      const token = await bootstrapTrackingSession(state.config.endpoint, {
        merchant_id: context.merchantId,
        api_key: context.apiKey,
      });
      if (token) {
        state.signedToken = token;
        state.status = "ready";
        void flush();
        return;
      }
    }
    if (state.widgetToken) {
      state.status = "ready";
      void flush();
      return;
    }
  } catch {
    // fall through to retry
  }
  state.status = "pending-auth";
  if (state.authRetryTimer) clearTimeout(state.authRetryTimer);
  state.authRetryTimer = setTimeout(() => void resolveAuth(), AUTH_RETRY_MS);
}

export async function flush(options: { beacon?: boolean } = {}): Promise<void> {
  if (state.status !== "ready" || !state.config || state.flushing) return;
  if (!getConsent()) {
    // Consent was revoked after events were queued: never transmit them.
    state.queue.clear();
    return;
  }
  // Never transmit events owned by a different merchant (e.g. left over from a
  // merchant change whose queue purge could not be persisted). Ownerless
  // pre-auth events belong to the current session's merchant.
  if (state.merchantId) {
    state.queue.dropWhere(
      (event) => event.owner !== undefined && event.owner !== state.merchantId,
    );
  }
  const batch = state.queue.take(batchSize());
  if (batch.length === 0) return;
  state.flushing = true;
  try {
    const payload: GepEventsPayload = {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      events: batch.map(toWireEvent),
    };
    const result = await postEvents(
      state.config.endpoint,
      { signedToken: state.signedToken, widgetToken: state.widgetToken },
      payload,
      options,
    );
    if (result.ok) {
      state.queue.remove(batch.map((event) => event.id));
      state.retryAttempt = 0;
    } else if (result.status === 401) {
      // Signed/widget token rejected: drop auth, keep the batch, and re-auth on
      // a backoff (never immediately, to avoid a tight 401 loop).
      state.signedToken = undefined;
      state.widgetToken = undefined;
      state.status = "pending-auth";
      if (state.authRetryTimer) clearTimeout(state.authRetryTimer);
      state.authRetryTimer = setTimeout(
        () => void resolveAuth(),
        AUTH_RETRY_MS,
      );
    } else {
      scheduleRetry();
    }
  } finally {
    state.flushing = false;
  }
}

function scheduleRetry(): void {
  if (state.retryTimer) return;
  const delay = Math.min(
    MAX_RETRY_DELAY_MS,
    1000 * Math.pow(2, state.retryAttempt),
  );
  state.retryAttempt += 1;
  state.retryTimer = setTimeout(() => {
    state.retryTimer = undefined;
    void flush();
  }, delay);
}

function heartbeat(): void {
  if (state.status === "disabled") return;
  trackEngagement();
}

function attachListeners(): void {
  if (typeof document === "undefined" || state.visibilityHandler) return;
  state.visibilityHandler = () => {
    if (document.visibilityState === "hidden") {
      trackEngagement();
      void flush({ beacon: true });
    } else {
      state.lastVisibleAt = Date.now();
    }
  };
  state.pageHideHandler = () => {
    trackEngagement();
    void flush({ beacon: true });
  };
  state.pageShowHandler = () => {
    // Returning from bfcache or resume: ignore the frozen gap so the next
    // heartbeat only counts time after the page became active again.
    state.lastVisibleAt = Date.now();
  };
  document.addEventListener("visibilitychange", state.visibilityHandler);
  window.addEventListener("pagehide", state.pageHideHandler);
  window.addEventListener("pageshow", state.pageShowHandler);
}

export function getGepTrackerStatus(): GepTrackerStatus {
  return state.status;
}

export function getGepQueueSize(): number {
  return state.queue.size();
}

export function resetGepTrackerForTests(): void {
  if (state.flushTimer) clearInterval(state.flushTimer);
  if (state.heartbeatTimer) clearInterval(state.heartbeatTimer);
  if (state.authRetryTimer) clearTimeout(state.authRetryTimer);
  if (state.retryTimer) clearTimeout(state.retryTimer);
  if (state.visibilityHandler && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", state.visibilityHandler);
  }
  if (state.pageHideHandler && typeof window !== "undefined") {
    window.removeEventListener("pagehide", state.pageHideHandler);
  }
  if (state.pageShowHandler && typeof window !== "undefined") {
    window.removeEventListener("pageshow", state.pageShowHandler);
  }
  state.config = undefined;
  state.status = "idle";
  state.merchantId = undefined;
  state.signedToken = undefined;
  state.widgetToken = undefined;
  state.flushTimer = undefined;
  state.heartbeatTimer = undefined;
  state.authRetryTimer = undefined;
  state.retryTimer = undefined;
  state.retryAttempt = 0;
  state.flushing = false;
  state.lastVisibleAt = Date.now();
  state.engagementAccumulatedMs = 0;
  state.visibilityHandler = undefined;
  state.pageHideHandler = undefined;
  state.pageShowHandler = undefined;
  lastPageLocation = "";
  resetConsentOverride();
  state.queue = new GepEventQueue(DEFAULT_MAX_QUEUE_SIZE);
  // Discard events queued before the last consent revocation, even if the
  // queue purge could not be persisted when consent was revoked.
  state.queue.dropBefore(getConsentRevokedAt());
  // Discard events queued before the last merchant change, even if the queue
  // purge could not be persisted when the merchant changed.
  state.queue.dropBefore(getMerchantChangedAt());
}
